import classNames from 'classnames';
import { type JSX, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@alltrails/denali/components/Button';
import Carousel from '@alltrails/denali/components/Carousel';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { ShopifyProduct } from '../../types/ShopifyProduct';
import { SHOP_STORE_URL } from '../../utils/constants';
import { formatShopifyProductsForDisplay } from '../../utils/formatProducts';
import ShopifyCard from '../ShopifyCard/ShopifyCard';
import styles from './ShopifyPreview.module.scss';

type ShopifyPreviewProps = {
  shopifyProducts: ShopifyProduct[];
  className?: string;
};

const ShopifyPreview = ({ shopifyProducts = [], className }: ShopifyPreviewProps): JSX.Element => {
  const isMobile = useIsMobileSizedScreen();

  // In the past Shopify has sent down [null] in the `images` array
  // so we filter the products to protect against that
  const formattedProducts = useMemo(
    () => formatShopifyProductsForDisplay(shopifyProducts),
    [shopifyProducts]
  );

  const visitTheStore = useMemo(
    () => (
      <div className={styles.storeLink}>
        <Button
          text={<FormattedMessage defaultMessage="Shop store" />}
          linkInfo={{
            href: SHOP_STORE_URL,
            target: '_blank'
          }}
          onClick={() => {
            /* do nothing - href opens link */
          }}
          testId="shopify-visit-store"
          variant="primary"
        />
      </div>
    ),
    []
  );

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.riverContainer}>
        <div className={styles.header}>
          <div className={styles.title}>
            <FormattedMessage defaultMessage="Gear up for the adventure" />
          </div>
          {!isMobile && visitTheStore}
        </div>

        <Carousel
          uniqueId="shopify-products-carousel"
          items={formattedProducts}
          renderItem={product => <ShopifyCard key={product.title} {...product} />}
          getItemKey={product => product.title}
          className={styles.riverClass}
          slidesPerViewMobile={1.2}
          showOverflow={false}
          showPaginationDots={false}
          slidesPerViewBreakpoints={{ 600: 2, 900: 3, 1230: 4 }}
        />
      </div>
      {isMobile && visitTheStore}
      <div className={styles.divider} />
    </div>
  );
};

export default ShopifyPreview;

